import React from "react";
import moment from "moment";
import {CopyOutlined, ProfileOutlined} from "@ant-design/icons";
import {Text, TextAs, TextSizes, IconButton} from "@clockout-dev/design-elements";
import SectionCard from "../../../common/sectionCard/SectionCard";
import JobStatusPill from "./JobStatusPill";
import {buildJobRowModel, truncateSha} from "../../audit.model";
import styles from "../../styles/document-audit.module.scss";

function formatTimestamp(value) {
    if (!value) return "—";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("MMM D, YYYY HH:mm:ss") : String(value);
}

function Row({label, children}) {
    return (
        <div className={styles.metaRow}>
            <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.metaLabel}>
                {label}
            </Text>
            <span className={styles.metaValue}>{children}</span>
        </div>
    );
}

function renderSummaryValue(value) {
    if (value === null || value === undefined) return "—";
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return String(value);
        }
    }
    return String(value);
}

/**
 * Metadata rail for the selected job: verdict, dimensions, hashes, batch,
 * forensics (ip / user agent), timestamps and the public-safe
 * `verdict_summary` bag rendered as small k/v rows.
 *
 * @param {Object} props
 * @param {Object} props.job — the selected job row
 * @param {(text: string, label: string) => void} props.onCopyText
 */
function JobMetadataCard({job, onCopyText}) {
    const model = buildJobRowModel(job);
    const summaryEntries = job?.verdict_summary && typeof job.verdict_summary === "object" && !Array.isArray(job.verdict_summary) ? Object.entries(job.verdict_summary) : [];

    return (
        <SectionCard icon={<ProfileOutlined />} title="Upload metadata">
            <div className={styles.metaGrid}>
                <Row label="Status">
                    <JobStatusPill job={job} />
                </Row>
                <Row label="Accepted">{job?.accepted === true ? "Yes" : job?.accepted === false ? "No" : "—"}</Row>
                {model.reasonLabel ? <Row label="Reason">{model.reasonLabel}</Row> : null}
                <Row label="Dimension">{job?.dimension || "—"}</Row>
                <Row label="MIME type">{job?.mime_type || "—"}</Row>
                <Row label="Retention">{model.retentionLabel}</Row>
                <Row label="SHA-256">
                    <span className={styles.shaValue} title={job?.sha256 || undefined}>
                        <span className={styles.mono}>{truncateSha(job?.sha256)}</span>
                        {job?.sha256 ? (
                            <IconButton icon={<CopyOutlined />} aria-label="Copy sha256" onClick={() => onCopyText(job.sha256, "SHA-256")} />
                        ) : null}
                    </span>
                </Row>
                <Row label="Batch">{job?.batch_id ? <span className={styles.mono}>{job.batch_id}</span> : "—"}</Row>
                <Row label="IP address">{job?.ip_address ? <span className={styles.mono}>{job.ip_address}</span> : "—"}</Row>
                <Row label="User agent">
                    <span className={styles.userAgent} title={job?.user_agent || undefined}>
                        {job?.user_agent || "—"}
                    </span>
                </Row>
                <Row label="Created">{formatTimestamp(job?.created_at)}</Row>
                <Row label="Completed">{formatTimestamp(job?.completed_at)}</Row>
            </div>

            {summaryEntries.length ? (
                <div className={styles.verdictBlock}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.verdictTitle}>
                        Verdict summary
                    </Text>
                    {summaryEntries.map(([key, value]) => (
                        <Text key={key} as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.verdictRow}>
                            <span className={styles.verdictKey}>{key}</span> {renderSummaryValue(value)}
                        </Text>
                    ))}
                </div>
            ) : null}
        </SectionCard>
    );
}

export default JobMetadataCard;
